import React, { useState, useEffect } from 'react';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import Loader from "../Shared/Loader/Loader";
import APIConstant from "../../utils/pathConstants";
import api from "../../utils/apiClient";
import { apiBase } from '../../utils/config';
import CommonConfig from '../../utils/constant';
import Toast from '../Shared/Toast/Toast';
import { useCatchResponse } from '../../utils/useCatchResponse';

const GRID = '40px minmax(0,1.8fr) minmax(0,2fr) minmax(0,1.3fr) 90px minmax(0,1.7fr) 52px';

const LMSSubmissions = ({ lmsId, schoolId, financialYear, userId }) => {
    const { catchResponse } = useCatchResponse();
    const [rows, setRows] = useState([]);
    const [isGradeSubject, setIsGradeSubject] = useState(false);
    const [maxMarks, setMaxMarks] = useState('');
    const [assignmentName, setAssignmentName] = useState('');
    const [dueDate, setDueDate] = useState(null);
    const [gradeOptions, setGradeOptions] = useState([]);

    useEffect(() => { load(); }, []);

    const load = async () => {
        try {
            Loader.show();
            const res = await api.post(APIConstant.path.GetAssignmentSubmissions, { LmsId: lmsId });
            if (res?.success) {
                setRows(res.data || []);
                setIsGradeSubject(res.IsGradeSubject === true);
                setMaxMarks(res.MaxMarks || '');
                setAssignmentName(res.AssignmentName || '');
                setDueDate(res.DueDate || null);
                if (res.IsGradeSubject === true) loadGrades();
            }
            Loader.hide();
        } catch (err) { Loader.hide(); catchResponse(err); }
    };

    const loadGrades = async () => {
        try {
            const res = await api.post(APIConstant.path.getGradeList, { UserID: schoolId, FinancialYearId: financialYear });
            if (res?.success) {
                const seen = new Set();
                const opts = (res.data || [])
                    .filter(y => !y?.IsCoScholastic)
                    .flatMap(x => (x?.GradeTemplateValues || []).map(v => ({ label: v?.GradeName, value: v?.GradeName })))
                    .filter(o => o.label && !seen.has(o.value) && seen.add(o.value));
                setGradeOptions(opts);
            }
        } catch (err) { }
    };

    const setField = (studentId, field, value) => {
        setRows(prev => prev.map(r => r.StudentId === studentId ? { ...r, [field]: value } : r));
    };

    const saveGrade = async (row) => {
        if (!isGradeSubject && maxMarks && (row.Marks || row.Marks === 0)) {
            const m = Number(row.Marks);
            if (isNaN(m) || m < 0) { Toast.error({ message: 'Enter a valid marks value' }); return; }
            if (m > Number(maxMarks)) { Toast.error({ message: `Marks cannot exceed the maximum marks (${maxMarks})` }); return; }
        }
        try {
            Loader.show();
            const res = await api.post(APIConstant.path.GradeSubmission, {
                LmsId: lmsId,
                StudentId: row.StudentId,
                Marks: isGradeSubject ? '' : (row.Marks || ''),
                Grade: isGradeSubject ? (row.Grade || '') : '',
                Feedback: row.Feedback || '',
                GradedBy: userId,
                SchoolId: schoolId,
                FinancialYearId: financialYear,
            });
            Loader.hide();
            if (res?.success) Toast.success({ message: res.message || 'Marks saved' });
            else Toast.error({ message: res?.message || 'Failed to save' });
        } catch (err) { Loader.hide(); catchResponse(err); }
    };

    const saveAll = async () => {
        const toSave = rows.filter(r => (r.SubmittedFiles || []).length &&
            (isGradeSubject ? (r.Grade || '').toString().trim() !== '' : (r.Marks !== '' && r.Marks != null)));
        if (!toSave.length) { Toast.error({ message: 'Enter marks for at least one student first' }); return; }
        if (!isGradeSubject && maxMarks) {
            const bad = toSave.find(r => isNaN(Number(r.Marks)) || Number(r.Marks) < 0 || Number(r.Marks) > Number(maxMarks));
            if (bad) { Toast.error({ message: `Marks cannot exceed the maximum marks (${maxMarks})` }); return; }
        }
        try {
            Loader.show();
            let ok = 0;
            for (const row of toSave) {
                const res = await api.post(APIConstant.path.GradeSubmission, {
                    LmsId: lmsId, StudentId: row.StudentId,
                    Marks: isGradeSubject ? '' : (row.Marks || ''),
                    Grade: isGradeSubject ? (row.Grade || '') : '',
                    Feedback: row.Feedback || '',
                    GradedBy: userId, SchoolId: schoolId, FinancialYearId: financialYear,
                }).catch(() => null);
                if (res?.success) ok++;
            }
            Loader.hide();
            Toast.success({ message: `Saved marks for ${ok} student(s)` });
        } catch (err) { Loader.hide(); catchResponse(err); }
    };

    const fmtDate = (d) => {
        if (!d) return '';
        const dt = new Date(d);
        return dt.toLocaleDateString('en-GB') + ' ' + dt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    };

    const submittedCount = rows.filter(r => (r.SubmittedFiles || []).length).length;

    const headStyle = { fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.02em' };
    const cell = { padding: '10px 8px', display: 'flex', alignItems: 'center' };

    return (
        <div>
            <div style={{ background: '#f0f7ff', border: '1px solid #bfd8f5', borderRadius: 10, padding: '12px 16px', marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#1c515e' }}>{assignmentName}</div>
                    <div style={{ fontSize: 12.5, color: '#475569', marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                        {!isGradeSubject && maxMarks ? <span><b>Max Marks:</b> {maxMarks}</span> : null}
                        {dueDate ? <span><b>Deadline:</b> {fmtDate(dueDate)}</span> : null}
                        <span><b>Submitted:</b> {submittedCount} / {rows.length}</span>
                    </div>
                </div>
                <Button label="Save All" icon="pi pi-save" className="btn-text-action-primary" disabled={submittedCount === 0} onClick={saveAll} />
            </div>

            <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
                {/* header */}
                <div style={{ display: 'grid', gridTemplateColumns: GRID, background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                    <div style={{ ...cell, ...headStyle }}>Sr.</div>
                    <div style={{ ...cell, ...headStyle }}>Student</div>
                    <div style={{ ...cell, ...headStyle }}>Submission</div>
                    <div style={{ ...cell, ...headStyle }}>Submitted On</div>
                    <div style={{ ...cell, ...headStyle }}>{isGradeSubject ? 'Grade' : 'Marks'}</div>
                    <div style={{ ...cell, ...headStyle }}>Remark</div>
                    <div style={{ ...cell, ...headStyle, justifyContent: 'center' }}></div>
                </div>

                {/* rows */}
                <div style={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'auto', overflowX: 'hidden' }}>
                    {rows.length === 0 && (
                        <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No students found for this assignment.</div>
                    )}
                    {rows.map((row, i) => {
                        const submitted = (row.SubmittedFiles || []).length > 0;
                        const over = !isGradeSubject && !!maxMarks && row.Marks !== '' && row.Marks != null && Number(row.Marks) > Number(maxMarks);
                        return (
                            <div key={row.StudentId} style={{ display: 'grid', gridTemplateColumns: GRID, borderBottom: '1px solid #f1f5f9', alignItems: 'center' }}>
                                <div style={{ ...cell, color: '#64748b', fontSize: 13 }}>{i + 1}</div>
                                <div style={{ ...cell, flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
                                    <span style={{ fontSize: 13.5, fontWeight: 600, color: '#1e2a44' }}>{row.Name || '-'}</span>
                                    {row.RollNo ? <span style={{ fontSize: 11.5, color: '#94a3b8' }}>Roll: {row.RollNo}</span> : null}
                                </div>
                                <div style={{ ...cell, flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
                                    {submitted
                                        ? row.SubmittedFiles.map((f, k) => (
                                            <a key={k} href={`${apiBase}/Upload/Submissions/${f}?token=${CommonConfig.fileToken()}`} target="_blank" rel="noopener noreferrer"
                                                style={{ fontSize: 12, color: '#2f6fed', textDecoration: 'none', wordBreak: 'break-all' }}>
                                                <i className="pi pi-file" style={{ fontSize: 11 }} /> {f.replace(/^\d+-/, '')}
                                            </a>
                                        ))
                                        : <span style={{ fontSize: 12, color: '#dc2626', fontWeight: 600 }}>Not submitted</span>}
                                </div>
                                <div style={{ ...cell, fontSize: 12, color: '#475569' }}>{row.SubmittedOn ? fmtDate(row.SubmittedOn) : '-'}</div>
                                <div style={{ ...cell, flexDirection: 'column', alignItems: 'stretch', gap: 2 }}>
                                    {isGradeSubject
                                        ? (gradeOptions.length
                                            ? <Dropdown value={row.Grade || ''} options={gradeOptions} className="p-w-100" placeholder="Select Grade"
                                                disabled={!submitted} filter showClear
                                                onChange={(e) => setField(row.StudentId, 'Grade', e.value || '')} />
                                            : <InputText value={row.Grade || ''} maxLength={5} className="p-w-100" placeholder="Grade" disabled={!submitted}
                                                onChange={(e) => setField(row.StudentId, 'Grade', e.target.value)} />)
                                        : (
                                            <>
                                                <InputText value={row.Marks || ''} keyfilter="pint" maxLength={4} className="p-w-100"
                                                    style={over ? { borderColor: '#dc2626' } : undefined}
                                                    placeholder={maxMarks ? `/${maxMarks}` : 'Marks'} disabled={!submitted}
                                                    onChange={(e) => setField(row.StudentId, 'Marks', e.target.value)} />
                                                {over ? <span style={{ color: '#dc2626', fontSize: 11 }}>Max {maxMarks}</span> : null}
                                            </>
                                        )}
                                </div>
                                <div style={cell}>
                                    <InputText value={row.Feedback || ''} maxLength={200} className="p-w-100" placeholder="Remark" disabled={!submitted}
                                        onChange={(e) => setField(row.StudentId, 'Feedback', e.target.value)} />
                                </div>
                                <div style={{ ...cell, justifyContent: 'center' }}>
                                    <Button icon="pi pi-check" className="btn-text-action-primary p-m-0" tooltip={over ? `Max ${maxMarks}` : 'Save'} tooltipOptions={{ position: 'top' }}
                                        disabled={!submitted || over} onClick={() => saveGrade(row)} />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default LMSSubmissions;
